import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { hexToRgba } from '../utils/hex-to-rgba';
import { staticColors } from '../styles/theme';
import { IToastProps } from '../interfaces/IToastProps';
import { IStaticThemeColors } from '../interfaces/IStaticThemeColors';
import { IFCWithChildren } from '../interfaces/IFCWithChildren';
import { AlertToast } from '../components/AlertToast';
import { TAlertToastTypes } from '../@types/AlertToastTypes';

interface IToastCallProps
  extends Omit<IToastProps, 'color' | 'iconColor' | 'visible' | 'forceClose'> {
  opacity?: number;
  withColorIcon?: boolean;
}

interface IToastContextData {
  showToast: (call: IToastCallProps) => void;
}

const colors: Record<TAlertToastTypes, keyof IStaticThemeColors> = {
  error: 'red',
  warning: 'orange',
  success: 'green',
  info: 'blue',
  neutral: 'grey',
};

const ToastContext = createContext({} as IToastContextData);

const ToastProvider: IFCWithChildren = ({ children }) => {
  const [toast, setToast] = useState<IToastProps>({} as IToastProps);

  const showToast = useCallback(
    (call: IToastCallProps) => {
      if (Object.keys(toast || {}).length > 0) {
        return;
      }

      const { type, opacity, withColorIcon } = call;
      const colorKey = colors[type];
      let color = staticColors[colorKey][500];
      const iconColor = withColorIcon ? color : staticColors.grey[0];
      if (opacity) {
        color = hexToRgba(color, opacity);
      }

      setToast({
        ...call,
        color,
        iconColor,
        forceClose: () => {
          setToast({} as IToastProps);
        },
      });
    },
    [toast]
  );

  const visible = useMemo(() => Object.keys(toast).length > 0, [toast]);

  return (
    <ToastContext.Provider
      value={{
        showToast,
      }}
    >
      <AlertToast visible={visible} {...toast} />
      {children}
    </ToastContext.Provider>
  );
};

const useToast = (): IToastContextData => {
  return useContext(ToastContext);
};

export { ToastProvider, useToast };
